import { HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CrudApiOptions, GenResp } from "@template-ui/services/crud-api.service";
import { AppBaseService } from "@app/shared/services/app-base.service";
import { map, Observable } from "rxjs";
import {
  LookupMetadataDomain,
  LookupMetadataDomainDetail,
  LookupMetadataEntry,
  LookupMetadataEntryUpsertRequest,
  mapLookupMetadataAdminError,
} from "./platform-lookup-metadata-admin.models";

const requestHeaders = new HttpHeaders({
  "Content-Type": "application/json",
  Accept: "application/json, text/plain, */*",
  "skip-error-interceptor": "yes",
  "skip-loading-interceptor": "yes",
});

const lookupMetadataAdminApiOptions: Partial<CrudApiOptions<"body", "json">> = {
  retries: 0,
  feedbackOptions: {
    successMode: "none",
    errorMode: "none",
  },
  getOptions: {
    responseType: "json",
    headers: requestHeaders,
    withCredentials: true,
  },
  postOptions: {
    responseType: "json",
    headers: requestHeaders,
    withCredentials: true,
  },
  putOptions: {
    responseType: "json",
    headers: requestHeaders,
    withCredentials: true,
  },
};

@Injectable({ providedIn: "root" })
export class PlatformLookupMetadataAdminService extends AppBaseService {
  getODataEndpoint(): string {
    return this.apis.core.manual.getPlatformConfigurationLookupMetadataEntriesOData();
  }

  listDomains(): Observable<LookupMetadataDomain[]> {
    return this.crudService.getbyUrl<LookupMetadataDomain[]>(
      this.apis.core.manual.getPlatformConfigurationLookupMetadataDomains(),
      lookupMetadataAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  getDomain(catalogTypeCode: string, domainCode: string): Observable<LookupMetadataDomainDetail> {
    return this.crudService.getbyUrl<LookupMetadataDomainDetail>(
      this.apis.core.manual.getPlatformConfigurationLookupMetadataDomain(catalogTypeCode, domainCode),
      lookupMetadataAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  createEntry(request: LookupMetadataEntryUpsertRequest): Observable<LookupMetadataEntry> {
    return this.crudService.postByUrl<LookupMetadataEntry>(
      this.apis.core.manual.postPlatformConfigurationLookupMetadataEntries(),
      request,
      lookupMetadataAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  updateEntry(
    catalogTypeCode: string,
    entryId: number,
    request: LookupMetadataEntryUpsertRequest,
  ): Observable<LookupMetadataEntry> {
    return this.crudService.putByUrl<LookupMetadataEntry>(
      this.apis.core.manual.putPlatformConfigurationLookupMetadataEntry(catalogTypeCode, entryId),
      request,
      lookupMetadataAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }
}

export { mapLookupMetadataAdminError };

function unwrapResponse<T>(response: GenResp<T> | T | undefined | null): T {
  if (response && typeof response === "object" && "data" in response) {
    return (response as GenResp<T>).data;
  }

  return response as T;
}
